import type { Household, OnboardingBody, User } from '@hewey/shared'
import { useState } from 'react'
import { ApiError, api } from '@api'
import { useAuth } from '@auth/AuthContext.tsx'
import {
	clearSignupIntent,
	readSignupIntent,
	readSuggestedName,
} from '@auth/intent.ts'
import { Field } from '@components/Field.tsx'
import { config } from '@config'
import { useRouter } from '@router'
import styles from './SignIn.module.css'

type Props = { user: User; household: Household }

type Mode = 'create' | 'join'

const messageFor = (e: unknown): string => {
	if (e instanceof ApiError) {
		if (e.code === 'invalid_code') {
			return `That family code doesn't match any household. Check it with whoever shared it, or start your own household instead.`
		}
		return e.message
	}
	return 'Something went wrong'
}

const normalizeCode = (raw: string): string =>
	raw
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, '')
		.slice(0, 6)

export const Onboarding = ({ user, household }: Props) => {
	const { setSession } = useAuth()
	const { navigate } = useRouter()
	// A fresh user always lands in their own unnamed household, so they choose:
	// name it (create) or abandon it to join someone else's (join). Normally
	// that choice was already made on the sign-in screen; the toggle below is
	// only for someone who closed the tab part-way and lost it — or who hit an
	// error (a bad family code, say) and needs a way off the path they're on.
	const isFounder = household.name === null
	const [chosenOnSignIn] = useState(readSignupIntent)
	const [mode, setMode] = useState<Mode>(chosenOnSignIn ?? 'create')
	const [displayName, setDisplayName] = useState(
		() => readSuggestedName() ?? (config.isDev ? 'Tom' : ''),
	)
	const [householdName, setHouseholdName] = useState('')
	const [code, setCode] = useState('')
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const joining = isFounder && mode === 'join'

	const submit = async (e: React.FormEvent) => {
		e.preventDefault()
		setBusy(true)
		setError(null)
		try {
			const body: OnboardingBody = joining
				? { mode: 'join', displayName: displayName.trim(), code }
				: {
						mode: 'create',
						displayName: displayName.trim(),
						householdName: isFounder
							? householdName.trim()
							: (household.name ?? ''),
					}
			const res = await api.authOnboarding(body)
			clearSignupIntent()
			setSession(res.user, res.household)
			navigate('/', { replace: true })
		} catch (e) {
			setError(messageFor(e))
		} finally {
			setBusy(false)
		}
	}

	const canSubmit =
		displayName.trim().length > 0 &&
		(joining
			? code.length === 6
			: !isFounder || householdName.trim().length > 0)

	return (
		<main className={styles.screen}>
			<div className={styles.card}>
				<div className={styles.brand}>
					<img src="/logo.png" alt="" className={styles.mark} />
					<h1 className={styles.title}>
						{joining
							? 'Join your family'
							: isFounder
								? 'Make yourself at home'
								: 'Welcome'}
					</h1>
				</div>

				{isFounder && (chosenOnSignIn === null || error !== null) ? (
					<div className={styles.segmented} role="tablist">
						<button
							type="button"
							role="tab"
							aria-selected={mode === 'create'}
							className={`${styles.segment} ${mode === 'create' ? styles.segmentActive : ''}`}
							onClick={() => setMode('create')}
						>
							Start a household
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={mode === 'join'}
							className={`${styles.segment} ${mode === 'join' ? styles.segmentActive : ''}`}
							onClick={() => setMode('join')}
						>
							Join with a code
						</button>
					</div>
				) : null}

				<p className={styles.hint}>
					{joining
						? `Enter the 6-character family code someone in your household shared with you.`
						: isFounder
							? `Name your household and pick what we should call you.`
							: `Joining ${household.name}. Pick what we should call you.`}
				</p>

				<form onSubmit={submit} className={styles.form}>
					{joining ? (
						<Field label="Family code">
							<input
								type="text"
								autoFocus
								required
								autoCapitalize="characters"
								autoComplete="off"
								value={code}
								onChange={(e) =>
									setCode(normalizeCode(e.target.value))
								}
								className={styles.codeInput}
								placeholder="ABC234"
							/>
						</Field>
					) : isFounder ? (
						<Field label="Household name">
							<input
								type="text"
								autoFocus
								required
								maxLength={100}
								value={householdName}
								onChange={(e) =>
									setHouseholdName(e.target.value)
								}
								placeholder="The Smiths"
							/>
						</Field>
					) : null}

					<Field label="Your name">
						<input
							type="text"
							required
							maxLength={100}
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							placeholder={user.email.split('@')[0]}
						/>
					</Field>

					<button
						type="submit"
						className={styles.button}
						disabled={busy || !canSubmit}
					>
						{busy ? 'Saving…' : 'Continue'}
					</button>
				</form>

				{error ? <p className={styles.error}>{error}</p> : null}
			</div>
		</main>
	)
}
